import React, { useContext, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useHistory } from 'react-router-dom';
import Title from '../../components/Title';
import { axiosKK } from '../../services/networkRequest';
import {
  AdminOxfordPackageOrdersResponse,
  Order,
  OrderListResponse,
  RemovalOrdersResponse,
  RemovalQuoteRequestsResponse,
} from '../../models/APIModels';
import Moment from 'moment';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import SubmitButton from '../../components/SubmitButton';
import FileDownload from 'js-file-download';
import { useApi } from '../../services/useApi';
import {
  MapPinIcon,
  TruckIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  TagIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';
import { DataTable, DataTableColumn } from '../../components/admin/Shared';
import { oxfordPaymentStatusLabel } from '../../utilities/OxfordPackageStatus';

type DashboardOrder = Order & {
  itemType?: 'quote' | 'oxford_package';
  tierName?: string;
  status?: string;
  customerEmail?: string;
  packageName?: string;
  paymentStatus?: string;
};

function AdminDashboardComponent({
  history,
}: RouteComponentProps) {
  const routerHistory = useHistory();
  const { user: loggedInUser } = useContext(UserContext);

  const { data: orders = [], loading, error } = useApi<DashboardOrder[]>(
    () =>
      Promise.all([
        axiosKK.get<OrderListResponse>('/orders').catch(() => ({ data: { orders: [] } })),
        axiosKK.get<RemovalOrdersResponse>('/admin/removals/orders').catch(() => ({ data: { orders: [] } })),
        axiosKK.get<RemovalQuoteRequestsResponse>('/admin/removals/quotes').catch(() => ({ data: { quotes: [] } })),
        // Admin-only on the API; a warehouse user would only be refused.
        loggedInUser?.role === Role.Admin
          ? axiosKK
              .get<AdminOxfordPackageOrdersResponse>('/admin/oxford-packages/orders')
              .catch(() => ({ data: { orders: [] } }))
          : Promise.resolve({ data: { orders: [] } }),
      ]).then(([regularOrdersResult, removalOrdersResult, quoteRequestsResult, oxfordOrdersResult]) => {
        const quotesWithType = (quoteRequestsResult.data.quotes || []).map((q) => ({
          ...q,
          itemType: 'quote' as const,
        }));
        const oxfordWithType = (oxfordOrdersResult.data.orders || []).map((o) => ({
          ...o,
          itemType: 'oxford_package' as const,
        }));
        const allOrders = [
          ...(regularOrdersResult.data.orders || []),
          ...(removalOrdersResult.data.orders || []),
          ...quotesWithType,
          ...oxfordWithType,
        ] as DashboardOrder[];
        return allOrders.sort((a, b) => b.createdAt - a.createdAt);
      }),
    [],
    'admin-dashboard-orders'
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigateToOrder = (order: DashboardOrder) => {
    if (order.itemType === 'quote') {
      routerHistory.push(`/admin/removals/quote/${order.id}`, { quote: order });
    } else if (order.itemType === 'oxford_package') {
      routerHistory.push(`/admin/oxford-packages/${order.id}`, { order });
    } else {
      routerHistory.push(`/admin/order/${order.id}`, { order });
    }
  };

  // Wrap a cell's content in a clickable span so the entire row is navigable.
  // DataTable owns the <tr> element; per-cell wrappers preserve the legacy
  // "click anywhere on the row" UX.
  const cell = (order: DashboardOrder, content: React.ReactNode): React.ReactNode => (
    <span
      className="admin-orders-row-cell"
      style={{ cursor: 'pointer', display: 'block' }}
      onClick={() => navigateToOrder(order)}
    >
      {content}
    </span>
  );

  const customerNameOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') {
      return order.customer
        ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim()
        : order.customerEmail || 'Unknown';
    }
    if (order.customer) {
      return `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim();
    }
    return 'Unknown';
  };

  const orderTypeOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return `Quote - ${order.tierName || 'Unknown'}`;
    if (order.itemType === 'oxford_package') return `Oxford Package - ${order.packageName || 'Unknown'}`;
    if (order.orderType === 'removal') return 'Removal';
    if (order.suitcaseOrder) return 'Suitcase';
    if (order.storageRequired) return 'Storage';
    return 'Shipping';
  };

  const collectionDateOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return 'Pending Quote';
    if (order.itemType === 'oxford_package') return 'N/A';
    if (order.orderType === 'removal') {
      return order.moveDate ? Moment(order.moveDate).format('dddd, MMM Do YYYY') : 'Not Set';
    }
    return order.collectionDate
      ? Moment(order.collectionDate).format('dddd, MMM Do YYYY')
      : 'Not Set';
  };

  const deliveryDateOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return (order.status || '').toUpperCase();
    if (order.itemType === 'oxford_package') return oxfordPaymentStatusLabel(order.paymentStatus);
    return order.deliveryDate
      ? Moment(order.deliveryDate).format('dddd, MMM Do YYYY')
      : 'Not Set';
  };

  const partnersBadge = (order: DashboardOrder): React.ReactNode => {
    if (order.itemType === 'quote' || order.orderType === 'removal') return '';
    if (order.itemType === 'oxford_package') return '';
    if (!order.storageRequired) return '';

    const hasCollection = order.collectionDriverId ?? order.assignedDriverId;
    const hasDelivery = order.deliveryDriverId ?? order.assignedDriverId;
    const hasWarehouse = order.assignedWarehouseId;
    const bothCouriers = hasCollection && hasDelivery;

    if (bothCouriers && hasWarehouse) {
      return <span className="assignment-badge assignment-full">All Assigned</span>;
    }
    if (bothCouriers && !hasWarehouse) {
      return <span className="assignment-badge assignment-couriers">Both Couriers</span>;
    }
    if (hasCollection && hasWarehouse) {
      return <span className="assignment-badge assignment-partial">Collection + Warehouse</span>;
    }
    if (hasDelivery && hasWarehouse) {
      return <span className="assignment-badge assignment-partial">Delivery + Warehouse</span>;
    }
    if (hasCollection) {
      return <span className="assignment-badge assignment-minimal">Collection Courier</span>;
    }
    if (hasDelivery) {
      return <span className="assignment-badge assignment-minimal">Delivery Courier</span>;
    }
    if (hasWarehouse) {
      return <span className="assignment-badge assignment-warehouse">Warehouse Only</span>;
    }
    return <span className="assignment-badge assignment-none">Unassigned</span>;
  };

  const fulfilledOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return 'N/A';
    if (order.itemType === 'oxford_package') return 'N/A';
    if (order.orderType === 'removal') return 'N/A';
    return order.roomPackIncluded ? 'Yes' : 'No';
  };

  const columns: DataTableColumn<DashboardOrder>[] = [
    { key: 'id', label: 'ID', render: (o) => cell(o, o.id) },
    {
      key: 'createdAt',
      label: 'Created Date',
      render: (o) => cell(o, o.createdAt ? Moment(o.createdAt).format('dddd, MMM Do YYYY') : 'N/A'),
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      render: (o) => cell(o, customerNameOf(o)),
    },
    {
      key: 'fulfilled',
      label: 'Fulfilled',
      render: (o) => cell(o, fulfilledOf(o)),
    },
    {
      key: 'orderType',
      label: 'Order Type',
      render: (o) => cell(o, orderTypeOf(o)),
    },
    {
      key: 'collectionDate',
      label: 'Collection Date',
      render: (o) => cell(o, collectionDateOf(o)),
    },
    {
      key: 'deliveryDate',
      label: 'Delivery Date',
      render: (o) => cell(o, deliveryDateOf(o)),
    },
    {
      key: 'partners',
      label: 'Partners',
      render: (o) => cell(o, partnersBadge(o)),
    },
  ];

  return (
    <>
      {(function () {
        switch (loggedInUser?.role) {
          case Role.Admin:
            return <Title>Admin Dashboard</Title>;
          case Role.Warehouse:
            return <Title>Warehouse Dashboard</Title>;
          default:
            return <Title>Dashboard</Title>;
        }
      })()}

      {loggedInUser && (
        <p className="grey">Welcome back, {loggedInUser?.firstName}</p>
      )}

      <section className="option-box-container">
        {loggedInUser?.role === Role.Admin && (
          <>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/logistics');
              }}
            >
              <div className="img">
                <TruckIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Logistics</h5>
                <p>Orders · Route Plan · Warehouse · Forecast</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/partners');
              }}
            >
              <div className="img">
                <UserGroupIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Partners</h5>
                <p>User Access · Pricing · Vehicles · Service Areas</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/invoices');
              }}
            >
              <div className="img">
                <BanknotesIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Invoices</h5>
                <p>Partner & warehouse monthly invoices</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/products');
              }}
            >
              <div className="img">
                <ArchiveBoxIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Products</h5>
                <p>Create and Edit Products</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/discounts');
              }}
            >
              <div className="img">
                <TagIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Discounts</h5>
                <p>Create and Edit Discounts</p>
              </div>
            </div>
            <div
              className="each-box"
              onClick={() => {
                history.push('/admin/users');
              }}
            >
              <div className="img">
                <ShieldCheckIcon width={24} height={24} />
              </div>
              <div className="content">
                <h5>Manage Admins</h5>
                <p>Create and Edit Admin Profiles</p>
              </div>
            </div>
          </>
        )}
        <div
          className="each-box"
          onClick={() => {
            history.push('/admin/customers');
          }}
        >
          <div className="img">
            <MapPinIcon width={24} height={24} />
          </div>
          <div className="content">
            <h5>Customer Profiles</h5>
            <p>Create and Edit Customer Profiles</p>
          </div>
        </div>
      </section>

      <div className="line" />

      <Title>Orders</Title>
      <section className="search-bar">
        <SubmitButton
          onClick={() => {
            axiosKK
              .get(`/orders/CSV`, {
                responseType: 'blob',
              })
              .then((response) => {
                FileDownload(response.data, `OrderBoxes.csv`);
              });
          }}
        >
          Download CSV
        </SubmitButton>
      </section>

      <DataTable<DashboardOrder>
        columns={columns}
        rows={orders}
        keyOf={(o) => `${o.itemType || o.orderType || 'order'}-${o.id}`}
        loading={loading}
        loadingMessage="Loading orders..."
        emptyMessage={
          error
            ? 'Failed to load orders. Please refresh the page.'
            : 'No orders found'
        }
      />
    </>
  );
}

export default withRouter(AdminDashboardComponent as any);
